import { defineStore } from "pinia";
import { ref } from "vue";
import { ITypeProtocol } from "../../models/sanitaire/typeProtocol";
import { ITypeProtocolApiResponse } from "../../models/api/typeProtocolApiResponse";
import { ITypeProtocolFilter } from "../../models/filters/sanitaire/typeProtocolFilter";
import TypeProtocolService from "../../services/sanitaire/TypeProtocolService";
import { ITypeProtocolForm } from "../../models/forms/sanitaire/typeProtocolForm";
import { ServiceOperationResult } from "../../models/api/serviceOperationResult";
import DataOperationError from "../../models/api/dataOperationError";
import { IDataOperationResult } from "../../models/api/dataOperationResult";
import signalRManager from "../../helper/signalRManager";
import IPayloadStore from "../../models/api/payloadStore";
import i18n from "@/i18n";

export const useTypeProtocolStore = defineStore("typeProtocols", () => {
    // State
    const datas = ref<ITypeProtocol[]>([]);
    const current = ref<ITypeProtocol>({} as ITypeProtocol);
    const itemForm = ref<ITypeProtocolForm>({} as ITypeProtocolForm);
    const isLoading = ref<boolean>(false);

    // Actions - Hydration
    async function hydrateTypeProtocols(items: ITypeProtocolApiResponse[]): Promise<ITypeProtocol[]> {
        if (items.length === 0) return [];
        return items.map(item => ({ ...item } as unknown as ITypeProtocol));
    }

    // Actions
    async function load(filter: ITypeProtocolFilter) {
        isLoading.value = true;
        let result = await TypeProtocolService.getAll<ITypeProtocolFilter, ITypeProtocolApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateTypeProtocols(result.data as ITypeProtocolApiResponse[]);
        }
        return result;
    }

    async function get(id: number) {
        let result = await TypeProtocolService.getById<ITypeProtocolApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateTypeProtocols([result.data as ITypeProtocolApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<ITypeProtocol | IDataOperationResult<ITypeProtocol>>;
        if (itemForm.value.id > 0) {
            result = await TypeProtocolService.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateTypeProtocols([result.data as ITypeProtocolApiResponse]);
                current.value = modelData;
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<ITypeProtocol>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await TypeProtocolService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateTypeProtocols([result.data as ITypeProtocolApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function deleteAction() {
        let result = await TypeProtocolService.delete<ITypeProtocolApiResponse>(current.value.id);
        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }
            current.value = newItem();
            itemForm.value = newForm();
        }
        return result;
    }

    function toForm(item: ITypeProtocol): ITypeProtocolForm {
        const form = {} as ITypeProtocolForm;
        form.id = item.id;
        form.code = item.code;
        form.libelle = item.libelle;
        return form;
    }

    function newForm(): ITypeProtocolForm {
        itemForm.value = {
            id: 0,
            code: "",
            libelle: ""
        } as ITypeProtocolForm;
        return itemForm.value;
    }

    function newItem(): ITypeProtocol {
        current.value = {
            id: 0,
            code: "",
            libelle: ""
        } as ITypeProtocol;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Actions
        hydrateTypeProtocols,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajTypeProtocol", async (payload: IPayloadStore) => {
    const store = useTypeProtocolStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateTypeProtocols([payload.data as ITypeProtocolApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateTypeProtocols([payload.data as ITypeProtocolApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
